import { fetchData } from "@utils/api";
import { APIMethods, ApiResponse } from "@typeDefinitions";
import * as yup from "yup";
import { CLIENT_PROFILE_EVENT_STATUS } from "@utils";
import { logFetchValidateError } from "@utils/validate";

export interface FetchClientEventsParams {
  type?: CLIENT_PROFILE_EVENT_STATUS[];
  page?: number;
}

export const fetchClientEvents = async (
  id: string,
  params?: FetchClientEventsParams,
): Promise<
  ApiResponse<
    (
      | SurveySentEventDto
      | SurveyRespondedEventDto
      | SurveyPlannedEventDto
      | PdfSentEventDto
      | EmptyEventDto
    )[]
  >
> => {
  const url = "/dietitian/patients/{patient_id}/events".replace(
    "{patient_id}",
    id.toString(),
  );

  const urlParams = mapParamsToUrlParams(params);

  const response = await fetchData(url, APIMethods.GET, undefined, urlParams);

  return await fetchClientEventsSchema
    .validate(response)
    .catch(logFetchValidateError("/dietitian/patients/{patient_id}/events"));
};

const mapParamsToUrlParams = (
  params?: FetchClientEventsParams,
): URLSearchParams | undefined => {
  if (!params) return undefined;
  const search = new URLSearchParams();
  if (params.type && params.type.length)
    params.type.forEach(t => search.append("type[]", String(t)));
  if (params.page !== undefined) search.set("page", String(params.page));
  return search;
};

const eventDataSchema = yup.object().shape({
  id: yup.number().required(),
  name: yup.string().required(),
});

const pdfEventDataSchema = yup.object().shape({
  name: yup.string().required(),
});

const clientEventSchema = yup.object().shape({
  id: yup.string().required(),
  data: eventDataSchema.required(),
  type: yup
    .number<
      | CLIENT_PROFILE_EVENT_STATUS.SURVEY_SENT
      | CLIENT_PROFILE_EVENT_STATUS.SURVEY_RESPONDED
      | CLIENT_PROFILE_EVENT_STATUS.SURVEY_PLANNED
    >()
    .required(),
  createdAt: yup.string().required(),
});

const pdfEventSchema = yup.object().shape({
  id: yup.string().required(),
  data: pdfEventDataSchema.required(),
  type: yup.number<CLIENT_PROFILE_EVENT_STATUS.PDF_SENT>().required(),
  createdAt: yup.string().required(),
});

const clientEmptyEventSchema = yup.object().shape({
  id: yup.string().required(),
  data: yup.number().nullable().default(null),
  type: yup.number<EmptyTypes>().required(),
  createdAt: yup.string().required(),
});

const fetchClientEventsSchema = yup.object().shape({
  data: yup
    .array()
    .of(
      yup.lazy(value => {
        switch (value.type) {
          case CLIENT_PROFILE_EVENT_STATUS.SURVEY_SENT:
            return clientEventSchema.required();
          case CLIENT_PROFILE_EVENT_STATUS.SURVEY_RESPONDED:
            return clientEventSchema.required();
          case CLIENT_PROFILE_EVENT_STATUS.SURVEY_PLANNED:
            return clientEventSchema.required();
          case CLIENT_PROFILE_EVENT_STATUS.PDF_SENT:
            return pdfEventSchema.required();
          default:
            return clientEmptyEventSchema.required();
        }
      }),
    )
    .required(),
});

export interface EventTypeDto {
  id: number;
}

export interface EventDto<T> {
  id: string;
  data: T;
  type: CLIENT_PROFILE_EVENT_STATUS;
  createdAt: string;
}

export interface SurveyVODto {
  id: number;
  name: string;
}

export interface ProgramPdfFileVODto {
  name: string;
}

export interface SurveySentEventDto extends EventDto<SurveyVODto> {
  type: CLIENT_PROFILE_EVENT_STATUS.SURVEY_SENT;
}

export interface SurveyRespondedEventDto extends EventDto<SurveyVODto> {
  type: CLIENT_PROFILE_EVENT_STATUS.SURVEY_RESPONDED;
}

export interface SurveyPlannedEventDto extends EventDto<SurveyVODto> {
  type: CLIENT_PROFILE_EVENT_STATUS.SURVEY_PLANNED;
}

export interface PdfSentEventDto extends EventDto<ProgramPdfFileVODto> {
  type: CLIENT_PROFILE_EVENT_STATUS.PDF_SENT;
}

export interface EmptyEventDto extends EventDto<number | null> {
  type: EmptyTypes;
}

export type EmptyTypes =
  | CLIENT_PROFILE_EVENT_STATUS.CREATED
  | CLIENT_PROFILE_EVENT_STATUS.ACTIVATED
  | CLIENT_PROFILE_EVENT_STATUS.DEACTIVATED
  | CLIENT_PROFILE_EVENT_STATUS.VISIT_CREATED
  | CLIENT_PROFILE_EVENT_STATUS.SUBSCRIPTION_ACTIVATED
  | CLIENT_PROFILE_EVENT_STATUS.PROGRAM_CREATED
  | CLIENT_PROFILE_EVENT_STATUS.MOBILE_APP_ACTIVATED
  | CLIENT_PROFILE_EVENT_STATUS.MOBILE_APP_DEACTIVATED
  | CLIENT_PROFILE_EVENT_STATUS.MEASUREMENT_ADDED;
